/**
 * Owner Dashboard Component
 * Complete customer management with financial data, analytics, and CSV import
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ownerAPI, adminAPI, studioAPI } from '../services/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import './OwnerDashboard.css';
import WebsiteManager from './WebsiteManager';
import OwnerStats from './OwnerStats';
import OwnerRevenue from './OwnerRevenue';
import { ChartCard, BarList, ColumnChart, LineChart, StatTile, StatGrid, ACCENT } from './charts/Charts';
import LeadsManager from './LeadsManager';
import EmailCampaigns from './EmailCampaigns';
import PartnerVerwaltung from './partner/PartnerVerwaltung';

const OwnerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegment = location.pathname.split('/owner/')[1] || '';
  const tabMap = { '': 'overview', stats: 'stats', customers: 'customers', revenue: 'revenue',
    bookings: 'bookings', import: 'import', studios: 'studios',
    settings: 'settings', log: 'log', nuki: 'nuki', tasks: 'tasks', backups: 'backups', website: 'website', leads: 'leads', campaigns: 'campaigns', partner: 'partner' };
  const activeTab = tabMap[pathSegment] || 'overview';
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Customer filters
  const [studioFilter, setStudioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [customerSort, setCustomerSort] = useState({ field: 'name', dir: 'asc' });

  // Customer editing
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '', last_name: '', email: '', password: '', role: 'Client', gender: '', studio_ids: [], status: 'Active',
    salutation: '', phone: '', birth_date: '', age: '', member_since: '', member_until: '',
    price: '', product: '', payment_method: '', iban: '', contract_type: '', cancellation_period: '',
    pt: '', todo: '', notes: '', newsletter_optout: false, is_superuser: false
  });
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());

  // CSV Import
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [csvHasHeader, setCsvHasHeader] = useState(true);
  const [csvStudioId, setCsvStudioId] = useState('');
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvErrors, setCsvErrors] = useState([]);



  // Nuki Smart Lock
  const [nukiLocks, setNukiLocks] = useState([]);
  const [nukiAuths, setNukiAuths] = useState([]);
  const [nukiLogs, setNukiLogs] = useState([]);
  const [nukiLoading, setNukiLoading] = useState(false);
  const [nukiLogFilter, setNukiLogFilter] = useState('');
  const [nukiLogLimit, setNukiLogLimit] = useState(50);
  const [nukiDailyStats, setNukiDailyStats] = useState([]);
  const [nukiUserMatches, setNukiUserMatches] = useState([]);
  const [nukiSubTab, setNukiSubTab] = useState('activity');
  const [selectedNukiLockId, setSelectedNukiLockId] = useState(null);
  const selectedNukiLockIdRef = useRef(null);

  // Settings (from Admin)
  const [settings, setSettings] = useState({
    slot_duration: '', booking_advance_days: '', cancellation_notice_hours: '',
    favicon_url: '', show_gender: '', app_url: '', header_title: '', header_subtitle: ''
  });

  // All bookings list (from Admin)
  const [allBookings, setAllBookings] = useState([]);
  const [bookingsFilter, setBookingsFilter] = useState('future');

  // Booking Log
  const [logBookings, setLogBookings] = useState([]);
  const [logSearch, setLogSearch] = useState('');
  const [logDate, setLogDate] = useState('');

  // Daily overview
  const [todayBookings, setTodayBookings] = useState([]);
  const [recentLogEntries, setRecentLogEntries] = useState([]);
  const [overviewNukiDailyStats, setOverviewNukiDailyStats] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);

  // Header Nuki (always visible)
  const [headerNukiLocks, setHeaderNukiLocks] = useState([]);
  const [nukiLockStudioMappings, setNukiLockStudioMappings] = useState({});

  // Studios CRUD (from Admin)
  const [newStudio, setNewStudio] = useState({ name: '', address: '', max_training_minutes_per_day: 0, max_peak_training_minutes_per_day: 0, slot_capacity: 3 });
  const [editingStudio, setEditingStudio] = useState(null);
  const [scheduleStudio, setScheduleStudio] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Booking CSV Import (from Admin)
  const [bookingCsvFile, setBookingCsvFile] = useState(null);
  const [bookingCsvPreview, setBookingCsvPreview] = useState(null);
  const [bookingColumnMapping, setBookingColumnMapping] = useState({});
  const [bookingCsvHasHeader, setBookingCsvHasHeader] = useState(true);
  const [bookingCsvErrors, setBookingCsvErrors] = useState([]);
  const [uploadingBookingCsv, setUploadingBookingCsv] = useState(false);
  const [bookingImportStudioId, setBookingImportStudioId] = useState('');
  const [bookingUnmatched, setBookingUnmatched] = useState(null);

  // Tasks sorting
  const [taskSort, setTaskSort] = useState({ field: 'client_name', dir: 'asc' });

  // Backups
  const [backups, setBackups] = useState([]);
  const [backupCreating, setBackupCreating] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);
  const lastFetchDateRef = useRef(format(new Date(), 'yyyy-MM-dd'));

  // Auto-refresh when tab becomes visible and date has changed (e.g. left open overnight)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = format(new Date(), 'yyyy-MM-dd');
        if (now !== lastFetchDateRef.current) {
          lastFetchDateRef.current = now;
          setReloadKey(k => k + 1);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const showMessage = (msg, type = 'success') => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // --- Settings handler ---
  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateSettings(settings);
      showMessage('Einstellungen gespeichert');
      setReloadKey(k => k + 1);
    } catch (err) { showMessage('Fehler beim Speichern', 'error'); }
  };

  // --- Booking handlers ---
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Buchung wirklich löschen?')) return;
    try {
      await adminAPI.deleteBooking(bookingId);
      showMessage('Buchung gelöscht');
      setReloadKey(k => k + 1);
    } catch (err) { showMessage('Fehler beim Löschen', 'error'); }
  };

  const handleExportReport = async () => {
    try {
      const response = await adminAPI.exportReport({});
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'buchungen-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage('Report exportiert');
    } catch (err) { showMessage('Fehler beim Exportieren', 'error'); }
  };

  // --- Booking CSV Import ---
  const bookingCsvDbFields = [
    { key: 'start_datetime', label: 'Start (Datum+Zeit)' },
    { key: 'end_datetime', label: 'Ende (Datum+Zeit)' },
    { key: 'email', label: 'E-Mail' },
    { key: 'name', label: 'Name' },
  ];

  const handleBookingCsvFileChange = (e) => {
    const file = e.target.files[0];
    setBookingCsvFile(file);
    setBookingCsvPreview(null);
    setBookingColumnMapping({});
    setBookingCsvHasHeader(true);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length === 0) return;
      const headers = parseCsvLine(lines[0]);
      const rows = lines.slice(1, 4).map(parseCsvLine);
      setBookingCsvPreview({ headers, rows });
      const autoMapping = {};
      headers.forEach((h, i) => {
        const lower = h.toLowerCase().trim();
        if (['beginn', 'start', 'von', 'begin', 'start date', 'startdatum'].includes(lower)) autoMapping[i] = 'start_datetime';
        else if (['ende', 'end', 'bis', 'end date', 'enddatum'].includes(lower)) autoMapping[i] = 'end_datetime';
        else if (['email', 'e-mail', 'mail', 'erstellt von', 'created by'].includes(lower)) autoMapping[i] = 'email';
        else if (['name', 'vorname', 'kunde', 'client'].includes(lower)) autoMapping[i] = 'name';
      });
      setBookingColumnMapping(autoMapping);
    };
    reader.readAsText(file);
  };

  const handleBookingCsvUpload = async (e) => {
    e.preventDefault();
    if (!bookingCsvFile) { showMessage('Bitte eine CSV-Datei auswählen', 'error'); return; }
    if (!bookingImportStudioId) { showMessage('Bitte ein Studio auswählen', 'error'); return; }
    const mappedFields = Object.values(bookingColumnMapping);
    if (!mappedFields.includes('start_datetime') || !mappedFields.includes('email')) {
      showMessage('Start (Datum+Zeit) und E-Mail müssen zugeordnet werden', 'error'); return;
    }
    setUploadingBookingCsv(true);
    setBookingCsvErrors([]);
    setBookingUnmatched(null);
    try {
      const formData = new FormData();
      formData.append('file', bookingCsvFile);
      formData.append('mapping', JSON.stringify(bookingColumnMapping));
      formData.append('hasHeader', bookingCsvHasHeader ? '1' : '0');
      formData.append('studioId', bookingImportStudioId);
      const response = await adminAPI.uploadBookingsCSV(formData);
      if (response.data.needsReview) {
        setBookingUnmatched(response.data);
        showMessage(`${response.data.unmatchedEmails.length} E-Mails konnten nicht zugeordnet werden`, 'error');
        return;
      }
      setBookingCsvFile(null); setBookingCsvPreview(null); setBookingColumnMapping({}); setBookingUnmatched(null);
      const fileInput = document.getElementById('booking-csv-file-input');
      if (fileInput) fileInput.value = '';
      if (response.data.errors && response.data.errors.length > 0) {
        setBookingCsvErrors(response.data.errors);
        showMessage(`${response.data.created} Buchungen erstellt, ${response.data.skipped} übersprungen — Details unten`, 'error');
      } else { showMessage(response.data.message); }
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Importieren der Buchungen', 'error');
    } finally { setUploadingBookingCsv(false); }
  };

  // --- Studio handlers ---
  const handleCreateStudio = async (e) => {
    e.preventDefault();
    try {
      await studioAPI.create(newStudio);
      showMessage('Studio erstellt');
      setNewStudio({ name: '', address: '', max_training_minutes_per_day: 0, max_peak_training_minutes_per_day: 0, slot_capacity: 3 });
      setReloadKey(k => k + 1);
    } catch (err) { showMessage('Fehler beim Erstellen', 'error'); }
  };

  const handleUpdateStudio = async (id, updates) => {
    try {
      await studioAPI.update(id, updates);
      showMessage('Studio aktualisiert');
      setEditingStudio(null);
      setReloadKey(k => k + 1);
    } catch (err) { showMessage('Fehler beim Aktualisieren', 'error'); }
  };

  const handleDeleteStudio = async (id) => {
    if (!window.confirm('Studio wirklich löschen?')) return;
    try {
      await studioAPI.delete(id);
      showMessage('Studio gelöscht');
      if (scheduleStudio === id) setScheduleStudio(null);
      setReloadKey(k => k + 1);
    } catch (err) { showMessage(err.response?.data?.error || 'Fehler beim Löschen', 'error'); }
  };

  const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  const handleOpenSchedule = async (studioId) => {
    if (scheduleStudio === studioId) { setScheduleStudio(null); return; }
    try {
      const res = await studioAPI.getSchedules(studioId);
      setSchedules(res.data);
      setScheduleStudio(studioId);
    } catch (err) { showMessage('Fehler beim Laden der Zeitpläne', 'error'); }
  };

  const handleAddScheduleBlock = (dayOfWeek) => {
    setSchedules([...schedules, { day_of_week: dayOfWeek, start_time: '08:00', end_time: '20:00', capacity: 3, is_peak: true }]);
  };

  const handleUpdateScheduleBlock = (index, field, value) => {
    const updated = [...schedules];
    updated[index] = { ...updated[index], [field]: value };
    setSchedules(updated);
  };

  const handleRemoveScheduleBlock = (index) => {
    setSchedules(schedules.filter((_, i) => i !== index));
  };

  const handleSaveSchedules = async () => {
    setSavingSchedule(true);
    try {
      const sentCount = schedules.length;
      await studioAPI.updateSchedules(scheduleStudio, schedules);
      const res = await studioAPI.getSchedules(scheduleStudio);
      const saved = res.data;
      setSchedules(saved);
      if (saved.length === sentCount) showMessage(`Stoßzeiten gespeichert: ${saved.length} Blöcke`);
      else showMessage(`ACHTUNG: ${sentCount} Blöcke gesendet, aber nur ${saved.length} in der DB gespeichert!`, 'error');
    } catch (err) {
      showMessage('Fehler beim Speichern: ' + (err.response?.data?.error || err.message), 'error');
    } finally { setSavingSchedule(false); }
  };

  // Load studios + Nuki header data once on mount (independent of tab data)
  useEffect(() => {
    studioAPI.getAll()
      .then(res => setStudios(res.data))
      .catch(err => console.error('Error loading studios:', err));
    Promise.all([
      ownerAPI.getNukiLocks().catch(() => ({ data: [] })),
      ownerAPI.getNukiLockStudioMappings().catch(() => ({ data: {} }))
    ]).then(([locksRes, mappingsRes]) => {
      setHeaderNukiLocks(locksRes.data || []);
      setNukiLockStudioMappings(mappingsRes.data || {});
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const today = format(new Date(), 'yyyy-MM-dd');
        lastFetchDateRef.current = today;
        const [dashRes, bookingsRes, locksRes] = await Promise.all([
          ownerAPI.getDashboard(),
          adminAPI.getAllBookings(),
          ownerAPI.getNukiLocks().catch(() => ({ data: [] }))
        ]);
        setDashboard(dashRes.data);
        // Filter today's confirmed bookings
        const todayB = (bookingsRes.data || []).filter(b => b.Date === today && b.Status === 'Confirmed');
        setTodayBookings(todayB);
        // Last 10 log entries (sorted by Created At desc)
        const sorted = [...(bookingsRes.data || [])].sort((a, b) => new Date(b['Created At'] || 0) - new Date(a['Created At'] || 0));
        setRecentLogEntries(sorted.slice(0, 10));
        // Nuki daily stats for ALL locks
        const allLockStats = [];
        for (const lock of (locksRes.data || [])) {
          try {
            const dailyRes = await ownerAPI.getNukiDailyStats(lock.id, { days: 1 });
            const stats = dailyRes.data;
            const todayStats = Array.isArray(stats) ? stats.find(s => s.date === today) : stats;
            if (todayStats) allLockStats.push({ lockId: lock.id, stats: todayStats });
          } catch { /* ignore */ }
        }
        setOverviewNukiDailyStats(allLockStats);
        // Fetch recent leads
        try {
          const leadsRes = await ownerAPI.getLeads();
          setRecentLeads((leadsRes.data || []).slice(0, 5));
        } catch { setRecentLeads([]); }
      } else if (activeTab === 'stats' || activeTab === 'revenue' || activeTab === 'tasks') {
        const dashRes = await ownerAPI.getDashboard();
        setDashboard(dashRes.data);
      } else if (activeTab === 'bookings') {
        const [dashRes, bookingsRes] = await Promise.all([
          ownerAPI.getDashboard(),
          adminAPI.getAllBookings()
        ]);
        setDashboard(dashRes.data);
        setAllBookings(bookingsRes.data);
      } else if (activeTab === 'settings') {
        const res = await adminAPI.getSettings();
        setSettings(res.data);
      } else if (activeTab === 'studios') {
        const res = await studioAPI.getAll();
        setStudios(res.data);
      } else if (activeTab === 'customers') {
        const custRes = await ownerAPI.getCustomers({ studio_id: studioFilter || undefined, status: statusFilter || undefined });
        setCustomers(custRes.data);
      } else if (activeTab === 'log') {
        const res = await adminAPI.getAllBookings();
        setLogBookings(res.data);
      } else if (activeTab === 'nuki') {
        setNukiLoading(true);
        try {
          const locksRes = await ownerAPI.getNukiLocks();
          setNukiLocks(locksRes.data);
          // Auto-select first lock if none selected
          const lockId = selectedNukiLockIdRef.current || (locksRes.data.length > 0 ? locksRes.data[0].id : null);
          if (lockId && !selectedNukiLockIdRef.current) {
            setSelectedNukiLockId(lockId);
            selectedNukiLockIdRef.current = lockId;
          }
          if (lockId) {
            const [authsRes, logsRes, dailyRes, matchesRes, custRes] = await Promise.all([
              ownerAPI.getNukiAuths(lockId),
              ownerAPI.getNukiLogs(lockId, { limit: nukiLogLimit }),
              ownerAPI.getNukiDailyStats(lockId, { months: 6 }).catch(() => ({ data: [] })),
              ownerAPI.getNukiUserMatches(lockId).catch(() => ({ data: [] })),
              ownerAPI.getCustomers({}).catch(() => ({ data: [] }))
            ]);
            setNukiAuths(authsRes.data);
            setNukiLogs(logsRes.data);
            setNukiDailyStats(dailyRes.data);
            setNukiUserMatches(matchesRes.data);
            setCustomers(custRes.data);
          }
        } catch (nukiErr) {
          console.error('Nuki load error:', nukiErr);
          const errMsg = nukiErr.response?.data?.error || nukiErr.message || 'Unbekannter Fehler';
          showMessage(`Nuki: ${errMsg}`, 'error');
        } finally {
          setNukiLoading(false);
        }
        return; // skip the general loading spinner
      } else if (activeTab === 'backups') {
        const res = await ownerAPI.getBackups();
        setBackups(res.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      showMessage('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  // Note: selectedNukiLockId and nukiLogLimit intentionally NOT dependencies here.
  // Lock switching and log pagination are handled by their own handlers to avoid race conditions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, studioFilter, statusFilter]);

  useEffect(() => { loadData(); }, [loadData, reloadKey]);

  // Auto-scroll to editing customer (e.g. when navigating from Tasks page)
  useEffect(() => {
    if (activeTab === 'customers' && editingCustomer && !loading) {
      setTimeout(() => {
        const el = document.getElementById(`customer-row-${editingCustomer}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [activeTab, editingCustomer, loading, customers]);

  // ========================
  // Customer Management
  // ========================

  const getFilteredCustomers = () => {
    let filtered = customers;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.last_name && c.last_name.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q))
      );
    }
    return filtered.sort((a, b) => {
      const aVal = (a[customerSort.field] || '').toString();
      const bVal = (b[customerSort.field] || '').toString();
      const cmp = aVal.localeCompare(bVal, 'de');
      return customerSort.dir === 'asc' ? cmp : -cmp;
    });
  };

  const handleSort = (field) => {
    setCustomerSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (field) => {
    if (customerSort.field !== field) return '';
    return customerSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const handleUpdateCustomer = async (id, updates) => {
    try {
      await ownerAPI.updateCustomer(id, updates);
      showMessage('Kunde aktualisiert');
      setEditingCustomer(null);
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Aktualisieren', 'error');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      await ownerAPI.createCustomer(newCustomer);
      showMessage('Kunde erstellt');
      setNewCustomer({
        name: '', last_name: '', email: '', password: '', role: 'Client', gender: '', studio_ids: [], status: 'Active',
        salutation: '', phone: '', birth_date: '', age: '', member_since: '', member_until: '',
        price: '', product: '', payment_method: '', iban: '', contract_type: '', cancellation_period: '',
        pt: '', todo: '', notes: '', newsletter_optout: false
      });
      setShowCreateForm(false);
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Erstellen', 'error');
    }
  };

  const handleDeleteCustomer = async (id) => {
    const customer = customers.find(c => c.id === id);
    const customerName = customer ? `${customer.name}${customer.last_name ? ' ' + customer.last_name : ''}` : '';
    if (!window.confirm(`Willst du "${customerName}" wirklich unwiderruflich löschen?`)) return;
    try {
      await ownerAPI.deleteCustomer(id);
      showMessage('Kunde gelöscht');
      setSelectedCustomers(prev => { const next = new Set(prev); next.delete(id); return next; });
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const handleDeleteSelectedCustomers = async () => {
    if (!window.confirm(`${selectedCustomers.size} Kunde(n) wirklich löschen?`)) return;
    try {
      await Promise.all([...selectedCustomers].map(id => ownerAPI.deleteCustomer(id)));
      showMessage(`${selectedCustomers.size} Kunde(n) gelöscht`);
      setSelectedCustomers(new Set());
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const toggleCustomerSelection = (id) => {
    setSelectedCustomers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllCustomers = (allIds) => {
    setSelectedCustomers(prev => prev.size === allIds.length ? new Set() : new Set(allIds));
  };


  const formatStatus = (status) => {
    const map = { Active: 'Aktiv', Cancelling: 'Kündigungsfrist', Cancelled: 'Ex', Prospect: 'Interessent', Paused: 'Pausiert' };
    return map[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // ========================
  // CSV Import
  // ========================

  const csvDbFields = [
    { key: 'email', label: 'E-Mail' },
    { key: 'vorname', label: 'Vorname' },
    { key: 'nachname', label: 'Nachname' },
    { key: 'vollname', label: 'Vollst. Name' },
    { key: 'status', label: 'Status' },
    { key: 'anmeldestatus', label: 'Anmeldestatus' },
    { key: 'hinweis', label: 'Hinweis' },
    { key: 'vertrag', label: 'Vertrag' },
    { key: 'zahlung', label: 'Zahlung' },
    { key: 'anrede', label: 'Anrede' },
    { key: 'eintritt', label: 'Eintritt' },
    { key: 'austritt', label: 'Austritt' },
    { key: 'preis', label: 'Preis' },
    { key: 'produkt', label: 'Produkt/Mitgliedschaft' },
    { key: 'kuendigungsfrist', label: 'Kündigungsfrist' },
    { key: 'telefon', label: 'Telefon' },
    { key: 'iban', label: 'IBAN' },
    { key: 'gebdatum', label: 'Geburtsdatum' },
    { key: 'todo', label: 'TODO' },
    { key: 'alter', label: 'Alter' },
    { key: 'pt', label: 'PT (Personal Training)' },
    { key: 'passwort', label: 'Passwort' },
    { key: 'nl_abbestellt', label: 'NL abbestellt' },
    { key: 'sepa_check', label: 'SEPA CHECK (→ Hinweis)' },
  ];

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if ((ch === ',' || ch === ';') && !inQuotes) { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    setCsvPreview(null);
    setColumnMapping({});
    setCsvHasHeader(true);
    setCsvErrors([]);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length === 0) return;

      const headers = parseCsvLine(lines[0]);
      const rows = lines.slice(1, 4).map(parseCsvLine);
      setCsvPreview({ headers, rows, totalRows: lines.length - 1 });

      // Auto-detect mapping
      const autoMapping = {};
      headers.forEach((h, i) => {
        const lower = h.toLowerCase().trim();
        if (['email', 'e-mail', 'mail'].includes(lower)) autoMapping[i] = 'email';
        else if (['vorname', 'firstname', 'first name'].includes(lower)) autoMapping[i] = 'vorname';
        else if (['nachname', 'lastname', 'last name'].includes(lower)) autoMapping[i] = 'nachname';
        else if (['vollständiger name', 'vollname', 'full name', 'name'].includes(lower)) autoMapping[i] = 'vollname';
        else if (['status'].includes(lower)) autoMapping[i] = 'status';
        else if (['anmeldestatus'].includes(lower)) autoMapping[i] = 'anmeldestatus';
        else if (['hinweis', 'note', 'notes'].includes(lower)) autoMapping[i] = 'hinweis';
        else if (['vertrag', 'contract'].includes(lower)) autoMapping[i] = 'vertrag';
        else if (['zahlung', 'payment'].includes(lower)) autoMapping[i] = 'zahlung';
        else if (['anrede', 'salutation'].includes(lower)) autoMapping[i] = 'anrede';
        else if (['eintritt', 'member since', 'join date'].includes(lower)) autoMapping[i] = 'eintritt';
        else if (['austritt', 'member until', 'leave date'].includes(lower)) autoMapping[i] = 'austritt';
        else if (['preis', 'price', 'betrag'].includes(lower)) autoMapping[i] = 'preis';
        else if (['produkt', 'product', 'mitgliedschaft', 'membership'].includes(lower)) autoMapping[i] = 'produkt';
        else if (['kündigungsfrist', 'kuendigungsfrist', 'cancellation'].includes(lower)) autoMapping[i] = 'kuendigungsfrist';
        else if (['telefon', 'phone', 'tel'].includes(lower)) autoMapping[i] = 'telefon';
        else if (['iban'].includes(lower)) autoMapping[i] = 'iban';
        else if (['geb. datum', 'geburtsdatum', 'birthday', 'birth date'].includes(lower)) autoMapping[i] = 'gebdatum';
        else if (['todo', 'aufgabe', 'task'].includes(lower)) autoMapping[i] = 'todo';
        else if (['alter', 'age'].includes(lower)) autoMapping[i] = 'alter';
        else if (['pt', 'personal training'].includes(lower)) autoMapping[i] = 'pt';
        else if (['passwort', 'password', 'pw'].includes(lower)) autoMapping[i] = 'passwort';
        else if (['nl abbestellt', 'newsletter abbestellt', 'nl'].includes(lower)) autoMapping[i] = 'nl_abbestellt';
        else if (lower.includes('sepa check') || lower.includes('sepa-check')) autoMapping[i] = 'sepa_check';
      });
      setColumnMapping(autoMapping);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) return;

    const mappedFields = Object.values(columnMapping);
    if (!mappedFields.includes('email')) {
      showMessage('E-Mail muss zugeordnet werden', 'error');
      return;
    }
    if (!csvStudioId) {
      showMessage('Bitte ein Studio auswählen', 'error');
      return;
    }

    setUploadingCsv(true);
    setCsvErrors([]);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('mapping', JSON.stringify(columnMapping));
      formData.append('hasHeader', csvHasHeader ? '1' : '0');
      formData.append('studioId', csvStudioId);

      const response = await ownerAPI.importCSV(formData);
      setCsvFile(null);
      setCsvPreview(null);
      setColumnMapping({});
      const fileInput = document.getElementById('owner-csv-file-input');
      if (fileInput) fileInput.value = '';

      if (response.data.errors && response.data.errors.length > 0) {
        setCsvErrors(response.data.errors);
        showMessage(`${response.data.created} erstellt, ${response.data.updated} aktualisiert, ${response.data.skipped} übersprungen`, 'error');
      } else {
        showMessage(response.data.message);
      }
      setReloadKey(k => k + 1);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Importieren', 'error');
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await ownerAPI.exportCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kunden-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage('Export heruntergeladen');
    } catch (err) {
      showMessage('Fehler beim Exportieren', 'error');
    }
  };

  // ========================
  // Render
  // ========================

  const renderOverview = () => {
    const today = new Date();
    const todayStr = format(today, 'EEEE, d. MMMM yyyy', { locale: de });

    // Group Nuki daily stats by studio using lock mappings
    const nukiStatsByStudio = {};
    overviewNukiDailyStats.forEach(({ lockId, stats }) => {
      const studioId = nukiLockStudioMappings[lockId] || 'unassigned';
      if (!nukiStatsByStudio[studioId]) nukiStatsByStudio[studioId] = { total: 0, persons: [] };
      nukiStatsByStudio[studioId].total += stats.total || 0;
      nukiStatsByStudio[studioId].persons = [...nukiStatsByStudio[studioId].persons, ...(stats.persons || [])];
    });

    return (
      <div className="owner-section">
        <div className="daily-overview-date">{todayStr}</div>

        {/* Per-Studio Cards (nebeneinander) */}
        <div className="studio-overview-grid">
          {studios.map(studio => {
            const perStudio = dashboard?.members?.per_studio?.find(s => s.studio_id === studio.id);
            const sd = perStudio?.status_distribution || {};
            const studioBookings = todayBookings.filter(b => b.studio_id === studio.id);
            const nukiStats = nukiStatsByStudio[studio.id];

            return (
              <div key={studio.id} className="studio-overview-card">
                <h2 className="studio-overview-card-title">{studio.name}</h2>
                <div className="studio-overview-card-stats">
                  <div className="studio-overview-stat">
                    <div className="studio-overview-stat-value">{(sd.active || 0) + (sd.cancelling || 0)}</div>
                    <div className="studio-overview-stat-label">Aktive Mitglieder</div>
                  </div>
                  <div className="studio-overview-stat">
                    <div className="studio-overview-stat-value success">{perStudio?.new_30_days || 0}</div>
                    <div className="studio-overview-stat-label">Neuzugänge (30T)</div>
                  </div>
                  <div className="studio-overview-stat">
                    <div className="studio-overview-stat-value danger">{perStudio?.cancellations_30_days || 0}</div>
                    <div className="studio-overview-stat-label">Kündigungen (30T)</div>
                  </div>
                  <div className="studio-overview-stat">
                    <div className="studio-overview-stat-value">{perStudio?.retention_rate || 0}%</div>
                    <div className="studio-overview-stat-label">Retention</div>
                  </div>
                </div>

                {/* Tages-Daten kompakt */}
                <div className="studio-overview-today">
                  <div className="studio-today-block">
                    <div className="studio-today-row">
                      <span className="studio-today-label">Check-ins (Nuki)</span>
                      <span className="studio-today-value">{nukiStats?.total || 0}</span>
                    </div>
                    {nukiStats && nukiStats.persons.length > 0 && (
                      <div className="studio-today-persons">
                        {nukiStats.persons.map((p, i) => (
                          <span key={i} className="studio-today-person">{typeof p === 'string' ? p : p.name || p}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="studio-today-block">
                    <div className="studio-today-row">
                      <span className="studio-today-label">Gebuchte Slots heute</span>
                      <span className="studio-today-value">{studioBookings.length}</span>
                    </div>
                  </div>
                  {/* Buchungen konsolidiert pro Person */}
                  {studioBookings.length > 0 && (() => {
                    const slotMin = parseInt(studio.slot_duration, 10) || 60;
                    const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                    const toStr = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                    const byPerson = {};
                    studioBookings.forEach(b => {
                      const name = b.clientName || 'Unbekannt';
                      if (!byPerson[name]) byPerson[name] = [];
                      const t = b['Start Time'] ? b['Start Time'].slice(0, 5) : null;
                      if (t) byPerson[name].push({ start: toMin(t), end: toMin(t) + slotMin });
                    });
                    const personRanges = Object.entries(byPerson).map(([name, intervals]) => {
                      intervals.sort((a, b) => a.start - b.start);
                      const merged = [intervals[0]];
                      for (let i = 1; i < intervals.length; i++) {
                        const last = merged[merged.length - 1];
                        if (intervals[i].start <= last.end) {
                          last.end = Math.max(last.end, intervals[i].end);
                        } else {
                          merged.push({ ...intervals[i] });
                        }
                      }
                      const rangeStr = merged.map(r => `${toStr(r.start)}\u2009\u2013\u2009${toStr(r.end)}`).join(', ');
                      return { name, rangeStr, firstStart: merged[0].start };
                    });
                    personRanges.sort((a, b) => a.firstStart - b.firstStart);
                    return (
                      <div className="studio-today-bookings-list">
                        {personRanges.map((p, i) => (
                          <div key={i} className="studio-today-booking">
                            <span className="studio-today-booking-name">{p.name}</span>
                            <span className="studio-today-booking-time">{p.rangeStr}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Neueste Anfragen */}
        <div className="daily-section">
          <h3 className="daily-section-title">
            Neueste Anfragen
            {recentLeads.length > 0 && <span className="daily-section-badge">{recentLeads.filter(l => l.status === 'new').length} neu</span>}
          </h3>
          {recentLeads.length > 0 ? (
            <div className="daily-activity-list">
              {recentLeads.map((lead, i) => {
                const date = lead.created_at ? format(new Date(lead.created_at), 'dd.MM. HH:mm') : '';
                const statusMap = { new: 'Neu', contacted: 'Kontaktiert', converted: 'Gewonnen', closed: 'Geschlossen' };
                const statusClass = lead.status === 'new' ? 'new-lead' : lead.status === 'converted' ? 'converted-lead' : '';
                return (
                  <div key={lead.id || i} className={`daily-activity-entry ${statusClass}`}>
                    <span className="daily-activity-time">{date}</span>
                    <span className="daily-activity-name">{lead.name}</span>
                    <span className="daily-activity-studio">{lead.phone}</span>
                    {lead.studio && <span className="daily-activity-slot">{lead.studio}</span>}
                    <span className={`lead-status-badge lead-status-${lead.status}`}>{statusMap[lead.status] || lead.status}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="daily-empty-state">Keine Anfragen</div>
          )}
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <button className="btn-link" onClick={() => navigate('/owner/leads')} style={{ fontSize: '13px', color: 'var(--color-accent, #c87a3e)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Alle Anfragen ansehen &rarr;
            </button>
          </div>
        </div>

        {/* Recent Activity Log (studio-übergreifend) */}
        <div className="daily-section">
          <h3 className="daily-section-title">Letzte Buchungsaktivitäten</h3>
          {recentLogEntries.length > 0 ? (
            <div className="daily-activity-list">
              {recentLogEntries.map((entry, i) => {
                const createdAt = entry['Created At'] ? new Date(entry['Created At']) : null;
                const timeStr = createdAt ? format(createdAt, 'dd.MM. HH:mm') : '';
                const slotDate = entry.Date ? format(new Date(entry.Date + 'T00:00:00'), 'dd.MM.', { locale: de }) : '';
                const slotTime = entry['Start Time'] ? entry['Start Time'].slice(0, 5) : '';
                const studioName = studios.find(s => s.id === entry.studio_id)?.name;
                return (
                  <div key={i} className={`daily-activity-entry ${entry.Status === 'Cancelled' ? 'cancelled' : ''}`}>
                    <span className="daily-activity-time">{timeStr}</span>
                    <span className="daily-activity-name">{entry.clientName || 'Unbekannt'}{entry.Persons > 1 && ` (${entry.Persons} Pers.)`}</span>
                    {studioName && <span className="daily-activity-studio">{studioName}</span>}
                    <span className="daily-activity-arrow">{entry.Status === 'Cancelled' ? '\u2717' : '\u2192'}</span>
                    <span className="daily-activity-slot">{slotDate} {slotTime}</span>
                    {entry.Status === 'Cancelled' && <span className="daily-activity-cancelled">storniert</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="daily-empty-state">Keine Aktivitäten</div>
          )}
        </div>
      </div>
    );
  };

  // Statistiken und Umsatz sind eigene Komponenten: OwnerStats.js / OwnerRevenue.js

  const renderBookings = () => {
    if (!dashboard) return null;
    const { bookings } = dashboard;

    return (
      <div className="owner-section">
        <h2>Buchungen</h2>

        <StatGrid>
          <StatTile hero label="Gesamt Buchungen" value={bookings.total} />
          <StatTile label="Bevorstehende" value={bookings.upcoming} tone="good" />
          <StatTile label="Diese Woche" value={bookings.this_week} />
          <StatTile label="Dieser Monat" value={bookings.this_month} />
          {bookings.per_studio.map(s => (
            <StatTile
              key={s.studio_id}
              label={s.studio_name}
              value={s.upcoming}
              sub={`bevorstehend · ${s.total} gesamt`}
            />
          ))}
        </StatGrid>

        <div style={{ marginTop: 30 }}>
          <ChartCard title="Beliebte Zeiten" subtitle="Meistgebuchte Startzeiten über alle Standorte">
            <BarList
              items={bookings.popular_times.map(t => ({ label: t.time, value: t.count }))}
              labelWidth={60}
              valueWidth={80}
              unit=" Buchungen"
            />
          </ChartCard>
        </div>

        {/* Booking list with management */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 40, marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>Alle Buchungen</h2>
          <button onClick={handleExportReport} className="btn-secondary">CSV Exportieren</button>
        </div>

        <div className="bookings-filter">
          <button className={bookingsFilter === 'future' ? 'active' : ''} onClick={() => setBookingsFilter('future')}>Zukünftig</button>
          <button className={bookingsFilter === 'past' ? 'active' : ''} onClick={() => setBookingsFilter('past')}>Vergangen</button>
          <button className={bookingsFilter === 'all' ? 'active' : ''} onClick={() => setBookingsFilter('all')}>Alle</button>
        </div>

        <div className="bookings-table">
          {allBookings
            .filter(booking => {
              const bookingDate = new Date(booking.Date);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              if (bookingsFilter === 'future') return bookingDate >= today;
              if (bookingsFilter === 'past') return bookingDate < today;
              return true;
            })
            .map(booking => (
              <div key={booking.id} className="booking-row">
                <div className="booking-col"><strong>{booking.clientName || 'Unbekannt'}</strong>{booking.Persons > 1 && ` (${booking.Persons} Pers.)`}</div>
                <div className="booking-col">{booking.Date ? format(new Date(booking.Date), 'd. MMM yyyy', { locale: de }) : '-'}</div>
                <div className="booking-col">{booking['Start Time']}</div>
                <div className="booking-col">
                  <span className={`status-badge ${(booking.Status || '').toLowerCase()}`}>{booking.Status}</span>
                </div>
                <div className="booking-col">
                  <button onClick={() => handleDeleteBooking(booking.id)} className="btn-delete">Löschen</button>
                </div>
              </div>
            ))}
        </div>

        {/* Booking CSV Import */}
        <h2 style={{ marginTop: 40 }}>Buchungen per CSV importieren</h2>
        <p style={{ fontSize: 14, color: '#718096', marginBottom: 15 }}>
          Importiere Buchungen aus einem externen System. E-Mail-Adressen werden gegen bestehende Kunden abgeglichen.
        </p>
        <form onSubmit={handleBookingCsvUpload} className="csv-upload-form">
          <select value={bookingImportStudioId} onChange={e => setBookingImportStudioId(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">-- Studio wählen --</option>
            {studios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input id="booking-csv-file-input" type="file" accept=".csv" onChange={handleBookingCsvFileChange} className="csv-file-input" />
          <button type="submit" className="btn-primary" disabled={!bookingCsvFile || uploadingBookingCsv || !bookingCsvPreview || !bookingImportStudioId}>
            {uploadingBookingCsv ? 'Importiere...' : 'Buchungen importieren'}
          </button>
        </form>

        {bookingCsvPreview && (
          <div className="csv-mapping">
            <div className="csv-mapping-header">
              <h4>Spalten zuordnen</h4>
              <label className="csv-header-toggle">
                <input type="checkbox" checked={bookingCsvHasHeader} onChange={e => setBookingCsvHasHeader(e.target.checked)} />
                Erste Zeile ist Überschrift
              </label>
            </div>
            <table className="csv-mapping-table">
              <thead><tr><th>CSV-Spalte</th><th>Vorschau</th><th>Zuordnung</th></tr></thead>
              <tbody>
                {bookingCsvPreview.headers.map((header, idx) => (
                  <tr key={idx}>
                    <td><strong>{bookingCsvHasHeader ? header : `Spalte ${idx + 1}`}</strong></td>
                    <td className="csv-preview-cell">{bookingCsvHasHeader ? (bookingCsvPreview.rows[0]?.[idx] || '–') : header}</td>
                    <td>
                      <select value={bookingColumnMapping[idx] || ''} onChange={e => {
                        const val = e.target.value;
                        setBookingColumnMapping(prev => { const next = { ...prev }; if (val) next[idx] = val; else delete next[idx]; return next; });
                      }}>
                        <option value="">– Nicht zuordnen –</option>
                        {bookingCsvDbFields.map(f => (
                          <option key={f.key} value={f.key} disabled={Object.entries(bookingColumnMapping).some(([k, v]) => v === f.key && Number(k) !== idx)}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(() => {
              const mapped = Object.values(bookingColumnMapping);
              const missing = bookingCsvDbFields.filter(f => ['start_datetime', 'email'].includes(f.key) && !mapped.includes(f.key));
              return missing.length > 0 ? <p className="csv-mapping-hint">Pflichtfelder noch zuordnen: {missing.map(f => f.label).join(', ')}</p> : null;
            })()}
          </div>
        )}

        {bookingUnmatched && (
          <div className="csv-errors" style={{ borderColor: '#f59e0b' }}>
            <div className="csv-errors-header">
              <h4>Nicht zugeordnete E-Mails ({bookingUnmatched.unmatchedEmails.length})</h4>
              <button onClick={() => setBookingUnmatched(null)} className="btn-secondary">Schließen</button>
            </div>
            <p style={{ fontSize: 13, marginBottom: 8 }}>{bookingUnmatched.matchedRows} von {bookingUnmatched.totalRows} Zeilen konnten zugeordnet werden. Folgende E-Mails haben keinen passenden Kunden:</p>
            <ul>{bookingUnmatched.unmatchedEmails.map((email, idx) => <li key={idx}><strong>{email}</strong></li>)}</ul>
            <p style={{ fontSize: 13, marginTop: 8 }}>Bitte lege die fehlenden Kunden zuerst an und importiere dann erneut.</p>
          </div>
        )}

        {bookingCsvErrors.length > 0 && (
          <div className="csv-errors">
            <div className="csv-errors-header">
              <h4>Details zum Import:</h4>
              <button onClick={() => setBookingCsvErrors([])} className="btn-secondary">Schließen</button>
            </div>
            <ul>{bookingCsvErrors.map((err, idx) => <li key={idx}>{err}</li>)}</ul>
          </div>
        )}
      </div>
    );
  };

  const renderSettings = () => (
    <div className="owner-section">
      <h2>Kalender Einstellungen</h2>
      <form onSubmit={handleUpdateSettings} className="settings-form">
        <div className="form-group">
          <label>Slot Dauer (Minuten)</label>
          <input type="number" value={settings.slot_duration} onChange={e => setSettings({ ...settings, slot_duration: e.target.value })} placeholder="30, 60 oder 90" />
        </div>
        <div className="form-group">
          <label>Buchung im Voraus (Tage)</label>
          <input type="number" value={settings.booking_advance_days} onChange={e => setSettings({ ...settings, booking_advance_days: e.target.value })} placeholder="14" />
        </div>
        <div className="form-group">
          <label>Stornierungsfrist (Stunden)</label>
          <input type="number" value={settings.cancellation_notice_hours} onChange={e => setSettings({ ...settings, cancellation_notice_hours: e.target.value })} placeholder="2" />
        </div>
        <div className="form-group">
          <label>Favicon URL</label>
          <input type="url" value={settings.favicon_url} onChange={e => setSettings({ ...settings, favicon_url: e.target.value })} placeholder="https://example.com/favicon.ico" />
        </div>
        <div className="form-group">
          <label>App URL (Custom Domain)</label>
          <input type="url" value={settings.app_url} onChange={e => setSettings({ ...settings, app_url: e.target.value })} placeholder="https://app.derstarkefranz.at" />
        </div>
        <div className="form-group">
          <label>Header Titel</label>
          <input type="text" value={settings.header_title} onChange={e => setSettings({ ...settings, header_title: e.target.value })} placeholder="Der Starke Franz" />
        </div>
        <div className="form-group">
          <label>Header Untertitel</label>
          <input type="text" value={settings.header_subtitle} onChange={e => setSettings({ ...settings, header_subtitle: e.target.value })} placeholder="Training buchen" />
        </div>
        <div className="form-group checkbox-group">
          <label>
            <input type="checkbox" checked={settings.show_gender === 'true' || settings.show_gender === true} onChange={e => setSettings({ ...settings, show_gender: e.target.checked ? 'true' : 'false' })} />
            Geschlecht bei Buchungen anzeigen
          </label>
        </div>
        <button type="submit" className="btn-primary">Speichern</button>
      </form>
    </div>
  );

  const renderStudios = () => (
    <div className="owner-section">
      <h2>Neues Studio erstellen</h2>
      <form onSubmit={handleCreateStudio} className="studio-form">
        <input type="text" placeholder="Name" value={newStudio.name} onChange={e => setNewStudio({ ...newStudio, name: e.target.value })} required />
        <input type="text" placeholder="Adresse" value={newStudio.address} onChange={e => setNewStudio({ ...newStudio, address: e.target.value })} />
        <div className="studio-form-row">
          <div className="form-group">
            <label>Max. Trainingszeit/Tag (Min, 0 = unbegrenzt)</label>
            <input type="number" min="0" value={newStudio.max_training_minutes_per_day} onChange={e => setNewStudio({ ...newStudio, max_training_minutes_per_day: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="form-group">
            <label>Max. Trainingszeit Stoßzeiten/Tag (Min, 0 = unbegrenzt)</label>
            <input type="number" min="0" value={newStudio.max_peak_training_minutes_per_day} onChange={e => setNewStudio({ ...newStudio, max_peak_training_minutes_per_day: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="form-group">
            <label>Verfügbare Slots pro Zeitfenster</label>
            <input type="number" min="1" value={newStudio.slot_capacity} onChange={e => setNewStudio({ ...newStudio, slot_capacity: parseInt(e.target.value) || 3 })} />
          </div>
        </div>
        <button type="submit" className="btn-primary">Studio erstellen</button>
      </form>

      <h2>Alle Studios ({studios.length})</h2>
      <div className="studios-table">
        {studios.map(studio => (
          <div key={studio.id} className={`studio-row ${editingStudio === studio.id ? 'editing' : ''}`}>
            {editingStudio === studio.id ? (
              <div className="studio-edit-form">
                <div className="studio-edit-row">
                  <div className="form-group"><label>Name</label><input type="text" defaultValue={studio.name} id={`studio-name-${studio.id}`} /></div>
                  <div className="form-group"><label>Adresse</label><input type="text" defaultValue={studio.address || ''} id={`studio-address-${studio.id}`} /></div>
                  <div className="form-group"><label>Max. Trainingszeit/Tag (Min)</label><input type="number" min="0" defaultValue={studio.max_training_minutes_per_day || 0} id={`studio-maxtraining-${studio.id}`} /></div>
                  <div className="form-group"><label>Max. Trainingszeit Stoßzeiten/Tag (Min)</label><input type="number" min="0" defaultValue={studio.max_peak_training_minutes_per_day || 0} id={`studio-maxpeak-${studio.id}`} /></div>
                  <div className="form-group"><label>Verfügbare Slots pro Zeitfenster</label><input type="number" min="1" defaultValue={studio.slot_capacity || 3} id={`studio-slotcap-${studio.id}`} /></div>
                </div>
                <div className="studio-edit-actions">
                  <button onClick={() => handleUpdateStudio(studio.id, {
                    name: document.getElementById(`studio-name-${studio.id}`).value,
                    address: document.getElementById(`studio-address-${studio.id}`).value,
                    max_training_minutes_per_day: parseInt(document.getElementById(`studio-maxtraining-${studio.id}`).value) || 0,
                    max_peak_training_minutes_per_day: parseInt(document.getElementById(`studio-maxpeak-${studio.id}`).value) || 0,
                    slot_capacity: parseInt(document.getElementById(`studio-slotcap-${studio.id}`).value) || 3
                  })} className="btn-primary">Speichern</button>
                  <button onClick={() => setEditingStudio(null)} className="btn-secondary">Abbrechen</button>
                </div>
              </div>
            ) : (
              <>
                <div className="studio-col studio-name"><strong>{studio.name}</strong></div>
                <div className="studio-col studio-address">{studio.address || '-'}</div>
                <div className="studio-col studio-limits">
                  <span className="restriction-badge">Slots: {studio.slot_capacity || 3}</span>
                  {studio.max_training_minutes_per_day > 0 && <span className="restriction-badge">Max: {studio.max_training_minutes_per_day} Min/Tag</span>}
                  {studio.max_peak_training_minutes_per_day > 0 && <span className="restriction-badge">Peak: {studio.max_peak_training_minutes_per_day} Min/Tag</span>}
                </div>
                <div className="studio-col studio-actions">
                  <button onClick={() => handleOpenSchedule(studio.id)} className="btn-secondary">{scheduleStudio === studio.id ? 'Stoßzeiten schließen' : 'Stoßzeiten'}</button>
                  <button onClick={() => setEditingStudio(studio.id)} className="btn-edit">Bearbeiten</button>
                  <button onClick={() => handleDeleteStudio(studio.id)} className="btn-delete">Löschen</button>
                </div>
              </>
            )}

            {scheduleStudio === studio.id && editingStudio !== studio.id && (
              <div className="schedule-editor" style={{ gridColumn: '1 / -1' }}>
                <h3>Stoßzeiten: {studio.name}</h3>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                  Slots sind immer 0–24 Uhr verfügbar. Hier legst du fest, welche Zeiträume als Stoßzeiten gelten.
                  {schedules.length > 0 && ` (${schedules.length} Blöcke, ${DAY_NAMES.filter((_, dayIdx) => schedules.some(s => s.day_of_week === dayIdx)).length} von 7 Tagen)`}
                </p>
                {DAY_NAMES.map((dayName, dayIdx) => {
                  const dayBlocks = schedules.map((s, origIdx) => ({ ...s, origIdx })).filter(s => s.day_of_week === dayIdx);
                  return (
                    <div key={dayIdx} className="schedule-day">
                      <div className="schedule-day-header">
                        <span className="day-name">{dayName}</span>
                        <button className="btn-add-block" onClick={() => handleAddScheduleBlock(dayIdx)} title="Zeitblock hinzufügen">+ Block</button>
                      </div>
                      {dayBlocks.length === 0 && <div className="schedule-empty">Keine Zeitblöcke</div>}
                      {dayBlocks.map(block => (
                        <div key={block.origIdx} className="schedule-block">
                          <input type="time" value={block.start_time} onChange={e => handleUpdateScheduleBlock(block.origIdx, 'start_time', e.target.value)} />
                          <span className="schedule-separator">–</span>
                          <input type="time" value={block.end_time} onChange={e => handleUpdateScheduleBlock(block.origIdx, 'end_time', e.target.value)} />
                          <label className="schedule-field">
                            <span>Kapazität</span>
                            <input type="number" min="1" value={block.capacity} onChange={e => handleUpdateScheduleBlock(block.origIdx, 'capacity', parseInt(e.target.value) || 1)} style={{ width: 60 }} />
                          </label>
                          <button className="btn-remove-block" onClick={() => handleRemoveScheduleBlock(block.origIdx)} title="Block entfernen">×</button>
                        </div>
                      ))}
                    </div>
                  );
                })}
                <div className="schedule-save">
                  <button className="btn-primary" onClick={handleSaveSchedules} disabled={savingSchedule}>{savingSchedule ? 'Speichere...' : 'Stoßzeiten speichern'}</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {studios.length === 0 && <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: 20 }}>Noch keine Studios vorhanden</p>}
      </div>
    </div>
  );

  const renderCustomers = () => {
    const filtered = getFilteredCustomers();
    const filteredIds = filtered.map(c => c.id);

    return (
      <div className="owner-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>Kunden</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {selectedCustomers.size > 0 && (
              <button onClick={handleDeleteSelectedCustomers} className="btn-delete">
                Ausgewählte löschen ({selectedCustomers.size})
              </button>
            )}
            <button onClick={handleExportCSV} className="btn-secondary">CSV Export</button>
            <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
              {showCreateForm ? 'Schließen' : '+ Neuer Kunde'}
            </button>
          </div>
        </div>

        {showCreateForm && (
          <form onSubmit={handleCreateCustomer} className="create-customer-form">
            <div className="form-section">
              <h3 className="form-section-title">Zugang</h3>
              <div className="form-section-grid">
                <div className="form-group"><label>E-Mail</label><input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} required={!!newCustomer.password} /></div>
                <div className="form-group"><label>Passwort</label><input type="password" value={newCustomer.password} onChange={(e) => setNewCustomer({ ...newCustomer, password: e.target.value })} /></div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}><small>Ohne E-Mail und Passwort wird der Kunde ohne App-Zugang angelegt (z. B. Partner) – für Tracking und Abbuchung. Ein Zugang lässt sich später über „Neues Passwort setzen“ ergänzen.</small></div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Studios</label><div className="studio-checkboxes">{studios.map(s => (<label key={s.id} className="studio-checkbox"><input type="checkbox" checked={newCustomer.studio_ids.includes(s.id)} onChange={(e) => { const ids = e.target.checked ? [...newCustomer.studio_ids, s.id] : newCustomer.studio_ids.filter(id => id !== s.id); setNewCustomer({ ...newCustomer, studio_ids: ids }); }} />{s.name}</label>))}</div></div>
                <div className="form-group"><label>Rolle</label><select value={newCustomer.role} onChange={(e) => setNewCustomer({ ...newCustomer, role: e.target.value })}><option value="Client">Client</option><option value="Team">Team (zählt nicht als Kunde)</option><option value="Owner">Owner</option></select></div>
                <div className="form-group"><label>Status</label><select value={newCustomer.status} onChange={(e) => setNewCustomer({ ...newCustomer, status: e.target.value })}><option value="Active">Aktiv</option><option value="Cancelling">Kündigungsfrist</option><option value="Paused">Pausiert</option><option value="Cancelled">Ex</option><option value="Inactive">Inaktiv</option><option value="Prospect">Interessent</option></select></div>
                <div className="form-group checkbox-group"><label><input type="checkbox" checked={newCustomer.is_superuser || false} onChange={(e) => setNewCustomer({ ...newCustomer, is_superuser: e.target.checked })} /> Superuser (keine Buchungs-Einschränkungen)</label></div>
              </div>
            </div>
            <div className="form-section">
              <h3 className="form-section-title">Persönliche Daten</h3>
              <div className="form-section-grid">
                <div className="form-group"><label>Vorname *</label><input type="text" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} required /></div>
                <div className="form-group"><label>Nachname</label><input type="text" value={newCustomer.last_name} onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })} /></div>
                <div className="form-group"><label>Anrede</label><select value={newCustomer.salutation} onChange={(e) => setNewCustomer({ ...newCustomer, salutation: e.target.value })}><option value="">-</option><option value="Liebe">Liebe</option><option value="Lieber">Lieber</option></select></div>
                <div className="form-group"><label>Geschlecht</label><select value={newCustomer.gender} onChange={(e) => setNewCustomer({ ...newCustomer, gender: e.target.value })}><option value="">-</option><option value="male">Männlich</option><option value="female">Weiblich</option><option value="other">Divers</option></select></div>
                <div className="form-group"><label>Geburtsdatum</label><input type="date" value={newCustomer.birth_date} onChange={(e) => setNewCustomer({ ...newCustomer, birth_date: e.target.value })} /></div>
                <div className="form-group"><label>Alter</label><input type="number" value={newCustomer.age} onChange={(e) => setNewCustomer({ ...newCustomer, age: e.target.value })} /></div>
                <div className="form-group"><label>Telefon</label><input type="text" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} /></div>
              </div>
            </div>
            <div className="form-section">
              <h3 className="form-section-title">Mitgliedschaft</h3>
              <div className="form-section-grid">
                <div className="form-group"><label>Mitglied seit</label><input type="date" value={newCustomer.member_since} onChange={(e) => setNewCustomer({ ...newCustomer, member_since: e.target.value })} /></div>
                <div className="form-group"><label>Austritt</label><input type="date" value={newCustomer.member_until} onChange={(e) => setNewCustomer({ ...newCustomer, member_until: e.target.value })} /></div>
                <div className="form-group"><label>Produkt</label><input type="text" value={newCustomer.product} onChange={(e) => setNewCustomer({ ...newCustomer, product: e.target.value })} placeholder="z.B. Laufend, 10er Block" /></div>
                <div className="form-group"><label>Vertrag</label><input type="text" value={newCustomer.contract_type} onChange={(e) => setNewCustomer({ ...newCustomer, contract_type: e.target.value })} /></div>
                <div className="form-group"><label>Kündigungsfrist (Monate)</label><input type="number" value={newCustomer.cancellation_period} onChange={(e) => setNewCustomer({ ...newCustomer, cancellation_period: e.target.value })} /></div>
                <div className="form-group"><label>PT</label><input type="text" value={newCustomer.pt} onChange={(e) => setNewCustomer({ ...newCustomer, pt: e.target.value })} placeholder="z.B. Ja, Gruppenboxen" /></div>
              </div>
            </div>
            <div className="form-section">
              <h3 className="form-section-title">Zahlung</h3>
              <div className="form-section-grid">
                <div className="form-group"><label>Preis (EUR)</label><input type="number" step="0.01" value={newCustomer.price} onChange={(e) => setNewCustomer({ ...newCustomer, price: e.target.value })} /></div>
                <div className="form-group"><label>Zahlungsart</label><select value={newCustomer.payment_method} onChange={(e) => setNewCustomer({ ...newCustomer, payment_method: e.target.value })}><option value="">-</option><option value="SEPA">SEPA</option><option value="Bar">Bar</option><option value="Überweisung">Überweisung</option></select></div>
                <div className="form-group"><label>IBAN</label><input type="text" value={newCustomer.iban} onChange={(e) => setNewCustomer({ ...newCustomer, iban: e.target.value })} /></div>
              </div>
            </div>
            <div className="form-section">
              <h3 className="form-section-title">Sonstiges</h3>
              <div className="form-section-grid">
                <div className="form-group" style={{ gridColumn: 'span 2' }}><label>TODO</label><input type="text" value={newCustomer.todo} onChange={(e) => setNewCustomer({ ...newCustomer, todo: e.target.value })} placeholder="z.B. ELBA, Vertrag fehlt" /></div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Notizen</label><textarea rows="2" value={newCustomer.notes} onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })} /></div>
                <div className="form-group checkbox-group"><label><input type="checkbox" checked={newCustomer.newsletter_optout} onChange={(e) => setNewCustomer({ ...newCustomer, newsletter_optout: e.target.checked })} /> NL abbestellt (kein Newsletter)</label></div>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">Kunde erstellen</button>
              <button type="button" className="btn-secondary" onClick={() => setShowCreateForm(false)}>Abbrechen</button>
            </div>
          </form>
        )}

        <div className="customer-filters">
          <input
            type="text"
            className="customer-search"
            placeholder="Suche nach Name, E-Mail, Telefon..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select value={studioFilter} onChange={(e) => { setStudioFilter(e.target.value); setReloadKey(k => k + 1); }}>
            <option value="">Alle Studios</option>
            {studios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setReloadKey(k => k + 1); }}>
            <option value="">Alle Status</option>
            <option value="Active,Cancelling">Aktiv + Kündigungsfrist</option>
            <option value="Active">Nur Aktiv</option>
            <option value="Cancelling">Nur Kündigungsfrist</option>
            <option value="Paused">Pausiert</option>
            <option value="Cancelled">Ex</option>
                        <option value="Prospect">Interessent</option>
          </select>
        </div>

        <div className="customer-count">{filtered.length} Kunden gefunden</div>

        <div className="table-scroll-container">
        <table className="customer-table">
          <thead>
            <tr>
              <th className="col-checkbox">
                <input type="checkbox" checked={filtered.length > 0 && selectedCustomers.size === filteredIds.length} onChange={() => toggleAllCustomers(filteredIds)} />
              </th>
              <th onClick={() => handleSort('name')}>Name{sortArrow('name')}</th>
              <th onClick={() => handleSort('email')}>E-Mail{sortArrow('email')}</th>
              <th onClick={() => handleSort('studio_name')}>Studio{sortArrow('studio_name')}</th>
              <th onClick={() => handleSort('role')}>Rolle{sortArrow('role')}</th>
              <th onClick={() => handleSort('status')}>Status{sortArrow('status')}</th>
              <th onClick={() => handleSort('member_since')}>Mitglied seit{sortArrow('member_since')}</th>
              <th onClick={() => handleSort('price')}>Preis{sortArrow('price')}</th>
              <th>Zahlung</th>
              <th onClick={() => handleSort('todo')}>TODO{sortArrow('todo')}</th>
              <th onClick={() => handleSort('age')}>Alter{sortArrow('age')}</th>
              <th onClick={() => handleSort('pt')}>PT{sortArrow('pt')}</th>
              <th>Geschlecht</th>
              <th>Telefon</th>
              <th onClick={() => handleSort('member_until')}>Austritt{sortArrow('member_until')}</th>
              <th>NL</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(customer => (
              editingCustomer === customer.id ? (
                <tr key={customer.id} id={`customer-row-${customer.id}`} className="editing">
                  <td colSpan="17">
                    <div className="customer-edit-form create-customer-form">
                      <div className="form-section">
                        <h3 className="form-section-title">Zugang</h3>
                        <div className="form-section-grid">
                          <div className="form-group"><label>E-Mail</label><input type="email" defaultValue={customer.email} id={`cust-email-${customer.id}`} /></div>
                          <div className="form-group"><label>Rolle</label><select defaultValue={customer.role || 'Client'} id={`cust-role-${customer.id}`}><option value="Client">Client</option><option value="Team">Team (zählt nicht als Kunde)</option><option value="Owner">Owner</option></select></div>
                          <div className="form-group"><label>Status</label><select defaultValue={customer.status} id={`cust-status-${customer.id}`}><option value="Active">Aktiv</option><option value="Cancelling">Kündigungsfrist</option><option value="Paused">Pausiert</option><option value="Cancelled">Ex</option><option value="Inactive">Inaktiv</option><option value="Prospect">Interessent</option></select></div>
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Studios</label><div className="studio-checkboxes" id={`cust-studios-${customer.id}`}>{studios.map(s => (<label key={s.id} className="studio-checkbox"><input type="checkbox" data-studio-id={s.id} defaultChecked={(customer.studios || []).some(cs => cs.id === s.id)} />{s.name}</label>))}</div></div>
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Neues Passwort setzen</label><input type="text" defaultValue="" id={`cust-password-${customer.id}`} placeholder="Leer lassen, um nicht zu ändern" autoComplete="new-password" /></div>
                          <div className="form-group checkbox-group"><label><input type="checkbox" defaultChecked={customer.is_superuser} id={`cust-su-${customer.id}`} /> Superuser (keine Buchungs-Einschränkungen)</label></div>
                        </div>
                      </div>
                      <div className="form-section">
                        <h3 className="form-section-title">Persönliche Daten</h3>
                        <div className="form-section-grid">
                          <div className="form-group"><label>Vorname</label><input type="text" defaultValue={customer.name} id={`cust-name-${customer.id}`} /></div>
                          <div className="form-group"><label>Nachname</label><input type="text" defaultValue={customer.last_name || ''} id={`cust-lastname-${customer.id}`} /></div>
                          <div className="form-group"><label>Anrede</label><select defaultValue={customer.salutation || ''} id={`cust-salutation-${customer.id}`}><option value="">-</option><option value="Liebe">Liebe</option><option value="Lieber">Lieber</option></select></div>
                          <div className="form-group"><label>Geschlecht</label><select defaultValue={customer.gender || ''} id={`cust-gender-${customer.id}`}><option value="">Nicht angegeben</option><option value="male">Männlich</option><option value="female">Weiblich</option><option value="other">Divers</option></select></div>
                          <div className="form-group"><label>Geburtsdatum</label><input type="date" defaultValue={customer.birth_date || ''} id={`cust-birth-${customer.id}`} /></div>
                          <div className="form-group"><label>Alter</label><input type="number" defaultValue={customer.age || ''} id={`cust-age-${customer.id}`} /></div>
                          <div className="form-group"><label>Telefon</label><input type="text" defaultValue={customer.phone || ''} id={`cust-phone-${customer.id}`} /></div>
                        </div>
                      </div>
                      <div className="form-section">
                        <h3 className="form-section-title">Mitgliedschaft</h3>
                        <div className="form-section-grid">
                          <div className="form-group"><label>Mitglied seit</label><input type="date" defaultValue={customer.member_since || ''} id={`cust-since-${customer.id}`} /></div>
                          <div className="form-group"><label>Austritt</label><input type="date" defaultValue={customer.member_until || ''} id={`cust-until-${customer.id}`} /></div>
                          <div className="form-group"><label>Produkt</label><input type="text" defaultValue={customer.product || ''} id={`cust-product-${customer.id}`} /></div>
                          <div className="form-group"><label>Vertrag</label><input type="text" defaultValue={customer.contract_type || ''} id={`cust-contract-${customer.id}`} /></div>
                          <div className="form-group"><label>Kündigungsfrist (Monate)</label><input type="number" defaultValue={customer.cancellation_period || ''} id={`cust-cancel-${customer.id}`} /></div>
                          <div className="form-group"><label>PT</label><input type="text" defaultValue={customer.pt || ''} id={`cust-pt-${customer.id}`} placeholder="z.B. Ja, Gruppenboxen, Zirkel" /></div>
                        </div>
                      </div>
                      <div className="form-section">
                        <h3 className="form-section-title">Zahlung</h3>
                        <div className="form-section-grid">
                          <div className="form-group"><label>Preis (EUR)</label><input type="number" step="0.01" defaultValue={customer.price || ''} id={`cust-price-${customer.id}`} /></div>
                          <div className="form-group"><label>Zahlungsart</label><select defaultValue={customer.payment_method || ''} id={`cust-payment-${customer.id}`}><option value="">-</option><option value="SEPA">SEPA</option><option value="Bar">Bar</option><option value="Überweisung">Überweisung</option></select></div>
                          <div className="form-group"><label>IBAN</label><input type="text" defaultValue={customer.iban || ''} id={`cust-iban-${customer.id}`} /></div>
                        </div>
                      </div>
                      <div className="form-section">
                        <h3 className="form-section-title">Sonstiges</h3>
                        <div className="form-section-grid">
                          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>TODO</label><input type="text" defaultValue={customer.todo || ''} id={`cust-todo-${customer.id}`} placeholder="z.B. ELBA, Vertrag fehlt" /></div>
                          <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Notizen</label><textarea rows="2" defaultValue={customer.notes || ''} id={`cust-notes-${customer.id}`} /></div>
                          <div className="form-group checkbox-group"><label><input type="checkbox" defaultChecked={customer.newsletter_optout} id={`cust-nl-${customer.id}`} /> NL abbestellt (kein Newsletter)</label></div>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn-primary"
                          onClick={() => handleUpdateCustomer(customer.id, {
                            name: document.getElementById(`cust-name-${customer.id}`).value,
                            last_name: document.getElementById(`cust-lastname-${customer.id}`).value,
                            email: document.getElementById(`cust-email-${customer.id}`).value,
                            studio_ids: Array.from(document.querySelectorAll(`#cust-studios-${customer.id} input[type=checkbox]:checked`)).map(cb => cb.dataset.studioId),
                            role: document.getElementById(`cust-role-${customer.id}`).value,
                            status: document.getElementById(`cust-status-${customer.id}`).value,
                            gender: document.getElementById(`cust-gender-${customer.id}`).value || null,
                            salutation: document.getElementById(`cust-salutation-${customer.id}`).value || null,
                            phone: document.getElementById(`cust-phone-${customer.id}`).value || null,
                            member_since: document.getElementById(`cust-since-${customer.id}`).value || null,
                            member_until: document.getElementById(`cust-until-${customer.id}`).value || null,
                            price: document.getElementById(`cust-price-${customer.id}`).value,
                            product: document.getElementById(`cust-product-${customer.id}`).value || null,
                            payment_method: document.getElementById(`cust-payment-${customer.id}`).value || null,
                            iban: document.getElementById(`cust-iban-${customer.id}`).value || null,
                            contract_type: document.getElementById(`cust-contract-${customer.id}`).value || null,
                            cancellation_period: document.getElementById(`cust-cancel-${customer.id}`).value,
                            birth_date: document.getElementById(`cust-birth-${customer.id}`).value || null,
                            age: document.getElementById(`cust-age-${customer.id}`).value,
                            pt: document.getElementById(`cust-pt-${customer.id}`).value || null,
                            todo: document.getElementById(`cust-todo-${customer.id}`).value || null,
                            notes: document.getElementById(`cust-notes-${customer.id}`).value || null,
                            newsletter_optout: document.getElementById(`cust-nl-${customer.id}`).checked,
                            is_superuser: document.getElementById(`cust-su-${customer.id}`).checked,
                            password: document.getElementById(`cust-password-${customer.id}`).value || undefined,
                          })}
                        >
                          Speichern
                        </button>
                        <button className="btn-secondary" onClick={() => setEditingCustomer(null)}>Abbrechen</button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={customer.id} id={`customer-row-${customer.id}`}>
                  <td className="col-checkbox">
                    <input type="checkbox" checked={selectedCustomers.has(customer.id)} onChange={() => toggleCustomerSelection(customer.id)} />
                  </td>
                  <td><span className="todo-client-link" onClick={() => setEditingCustomer(customer.id)}><strong>{customer.name}</strong>{customer.last_name ? ` ${customer.last_name}` : ''}</span></td>
                  <td>{customer.email || <span className="customer-no-access">ohne Zugang</span>}</td>
                  <td>{customer.studios?.length > 0 ? customer.studios.map(s => s.name).join(', ') : customer.studio_name || '-'}</td>
                  <td><span className={`role-badge ${(customer.role || '').toLowerCase()}`}>{customer.role}</span>{customer.is_superuser && <span className="role-badge superuser">SU</span>}</td>
                  <td><span className={`customer-status ${(customer.status || '').toLowerCase()}`}>{formatStatus(customer.status)}</span></td>
                  <td>{formatDate(customer.member_since)}</td>
                  <td>{formatCurrency(customer.price)}</td>
                  <td>{customer.payment_method || '-'}</td>
                  <td>{customer.todo ? <span className="customer-todo">{customer.todo}</span> : '-'}</td>
                  <td>{customer.age || '-'}</td>
                  <td>{customer.pt || '-'}</td>
                  <td>{customer.gender === 'male' ? 'M' : customer.gender === 'female' ? 'W' : customer.gender === 'other' ? 'D' : '-'}</td>
                  <td>{customer.phone || '-'}</td>
                  <td>{formatDate(customer.member_until)}</td>
                  <td>{customer.newsletter_optout ? 'Ja' : '-'}</td>
                  <td className="col-actions">
                    <button className="btn-icon" onClick={() => setEditingCustomer(customer.id)} title="Bearbeiten">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="btn-icon btn-icon-delete" onClick={() => handleDeleteCustomer(customer.id)} title="Löschen">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
        </div>

      </div>
    );
  };

  const renderImport = () => {
    return (
      <div className="owner-section">
        <h2>CSV Import</h2>
        <p style={{ fontSize: 14, color: '#718096', marginBottom: 15 }}>
          Importiere Kunden aus Airtable Grid-View CSV-Dateien. Bestehende Kunden (gleiche E-Mail) werden aktualisiert.
        </p>

        <div className="customer-filters">
          <select value={csvStudioId} onChange={(e) => setCsvStudioId(e.target.value)}>
            <option value="">Studio auswählen *</option>
            {studios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <form onSubmit={handleCsvUpload} className="csv-upload-form">
          <input
            id="owner-csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleCsvFileChange}
            className="csv-file-input"
          />
          <button type="submit" className="btn-primary" disabled={!csvFile || uploadingCsv || !csvPreview || !csvStudioId}>
            {uploadingCsv ? 'Importiere...' : 'CSV importieren'}
          </button>
        </form>

        {csvPreview && (
          <div className="csv-mapping">
            <div className="csv-mapping-header">
              <h4>Spalten zuordnen ({csvPreview.totalRows} Zeilen erkannt)</h4>
              <label className="csv-header-toggle">
                <input type="checkbox" checked={csvHasHeader} onChange={(e) => setCsvHasHeader(e.target.checked)} />
                Erste Zeile ist Überschrift
              </label>
            </div>
            <table className="csv-mapping-table">
              <thead>
                <tr>
                  <th>CSV-Spalte</th>
                  <th>Vorschau</th>
                  <th>Zuordnung</th>
                </tr>
              </thead>
              <tbody>
                {csvPreview.headers.map((header, idx) => (
                  <tr key={idx}>
                    <td><strong>{csvHasHeader ? header : `Spalte ${idx + 1}`}</strong></td>
                    <td className="csv-preview-cell">{csvHasHeader ? (csvPreview.rows[0]?.[idx] || '\u2013') : header}</td>
                    <td>
                      <select
                        value={columnMapping[idx] || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setColumnMapping(prev => {
                            const next = { ...prev };
                            if (val) { next[idx] = val; } else { delete next[idx]; }
                            return next;
                          });
                        }}
                      >
                        <option value="">{'\u2013'} Nicht zuordnen {'\u2013'}</option>
                        {csvDbFields.map(f => (
                          <option
                            key={f.key}
                            value={f.key}
                            disabled={Object.entries(columnMapping).some(([k, v]) => v === f.key && Number(k) !== idx)}
                          >
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!Object.values(columnMapping).includes('email') && (
              <p className="csv-mapping-hint">Pflichtfeld: E-Mail muss zugeordnet werden</p>
            )}
          </div>
        )}

        {csvErrors.length > 0 && (
          <div className="csv-errors">
            <div className="csv-errors-header">
              <h4>Import-Details:</h4>
              <button onClick={() => setCsvErrors([])} className="btn-secondary">Schließen</button>
            </div>
            <ul>
              {csvErrors.map((err, idx) => <li key={idx}>{err}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const nukiActionClass = (code) => {
    if (code === 1) return 'unlock';
    if (code === 2) return 'lock';
    if (code === 3) return 'unlatch';
    return 'other';
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  const handleSwitchNukiLock = async (lockId) => {
    selectedNukiLockIdRef.current = lockId;
    setSelectedNukiLockId(lockId);
    setNukiLoading(true);
    try {
      const [authsRes, logsRes, dailyRes, matchesRes] = await Promise.all([
        ownerAPI.getNukiAuths(lockId),
        ownerAPI.getNukiLogs(lockId, { limit: 50 }),
        ownerAPI.getNukiDailyStats(lockId, { months: 6 }).catch(err => {
          console.error('Nuki daily stats error:', err);
          return { data: [] };
        }),
        ownerAPI.getNukiUserMatches(lockId).catch(() => ({ data: [] })),
      ]);
      setNukiAuths(authsRes.data);
      setNukiLogs(logsRes.data);
      setNukiDailyStats(dailyRes.data || []);
      setNukiUserMatches(matchesRes.data);
      setNukiLogLimit(50);
      setNukiLogFilter('');
    } catch (err) {
      console.error('Nuki switch lock error:', err);
      showMessage('Fehler beim Laden der Nuki-Daten', 'error');
    } finally {
      setNukiLoading(false);
    }
  };

  const loadMoreNukiLogs = async () => {
    if (!selectedNukiLockId) return;
    const newLimit = nukiLogLimit + 50;
    setNukiLogLimit(newLimit);
    try {
      const logsRes = await ownerAPI.getNukiLogs(selectedNukiLockId, { limit: newLimit });
      setNukiLogs(logsRes.data);
    } catch (err) {
      showMessage('Fehler beim Laden', 'error');
    }
  };

  const handleSaveNukiMatch = async (nukiAuthId, clientId) => {
    try {
      await ownerAPI.saveNukiUserMatch(nukiAuthId, clientId || null);
      showMessage('Zuordnung gespeichert');
      if (selectedNukiLockId) {
        const res = await ownerAPI.getNukiUserMatches(selectedNukiLockId);
        setNukiUserMatches(res.data);
      }
    } catch (err) {
      showMessage('Fehler beim Speichern', 'error');
    }
  };

  const renderNuki = () => {
    if (nukiLoading) return <div className="loading">Lade Nuki-Daten...</div>;

    const lock = nukiLocks.find(l => l.id === selectedNukiLockId) || nukiLocks[0];
    const filteredLogs = nukiLogFilter
      ? nukiLogs.filter(l => l.name && l.name.toLowerCase().includes(nukiLogFilter.toLowerCase()))
      : nukiLogs;

    // Daily stats helpers - split into recent (14 days) and full history
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const todayStats = nukiDailyStats.find(d => d.date === today);
    const yesterdayStats = nukiDailyStats.find(d => d.date === yesterday);
    const last7 = nukiDailyStats.filter(d => {
      const diff = (new Date(today) - new Date(d.date)) / 86400000;
      return diff >= 0 && diff < 7;
    });
    const last7Persons = [...new Set(last7.flatMap(d => d.persons || []))].sort((a, b) => a.localeCompare(b, 'de'));
    const last7Unique = last7Persons.length;
    const recentDays = nukiDailyStats.slice(0, 14);
    // All-time stats (for when there's no recent data)
    const allPersons = [...new Set(nukiDailyStats.flatMap(d => d.persons || []))];
    const hasRecentData = todayStats || yesterdayStats || last7.length > 0;
    const lastActivityDate = nukiDailyStats.length > 0 ? nukiDailyStats[0].date : null;

    // Monthly aggregation for trend chart
    const monthlyAgg = {};
    nukiDailyStats.forEach(d => {
      const month = d.date.substring(0, 7);
      if (!monthlyAgg[month]) {
        monthlyAgg[month] = { month, totalDays: 0, totalVisitors: 0, uniquePersons: new Set() };
      }
      monthlyAgg[month].totalDays++;
      monthlyAgg[month].totalVisitors += d.total;
      (d.persons || []).forEach(p => monthlyAgg[month].uniquePersons.add(p));
    });
    const monthlyData = Object.values(monthlyAgg)
      .map(m => ({
        month: m.month,
        label: new Date(m.month + '-01').toLocaleDateString('de-AT', { month: 'short', year: '2-digit' }),
        avgDaily: Math.round(m.totalVisitors / m.totalDays * 10) / 10,
        uniqueTotal: m.uniquePersons.size,
        totalVisits: m.totalVisitors
      }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Resolve studio name for selected lock
    const selectedStudioId = nukiLockStudioMappings[lock?.id];
    const selectedStudio = studios.find(s => s.id === selectedStudioId);

    return (
      <div className="owner-section">
        <h2>Nuki Smart Lock</h2>

        {/* Lock Selector (shown when multiple locks available) */}
        {nukiLocks.length > 1 && (
          <div className="nuki-lock-selector">
            {nukiLocks.map(l => {
              const sId = nukiLockStudioMappings[l.id];
              const s = studios.find(st => st.id === sId);
              const battClass = l.battery > 60 ? 'good' : l.battery > 30 ? 'medium' : l.battery > 10 ? 'low' : 'critical';
              return (
                <button
                  key={l.id}
                  className={`nuki-lock-selector-btn ${l.id === lock?.id ? 'active' : ''}`}
                  onClick={() => handleSwitchNukiLock(l.id)}
                >
                  <span className="lock-selector-name">{s?.name || l.name}</span>
                  <span className={`lock-selector-battery ${battClass}`}>{l.battery}%</span>
                  <span className={`lock-selector-status ${l.state === 1 ? 'locked' : 'unlocked'}`}>
                    {l.state === 1 ? 'Gesperrt' : l.state === 3 ? 'Offen' : '?'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {lock && (
          <div className="nuki-lock-info">
            <span className="lock-name">{selectedStudio?.name || lock.name}</span>
            <span className={`lock-status ${lock.state === 1 ? 'locked' : 'unlocked'}`}>
              {lock.state === 1 ? 'Gesperrt' : lock.state === 3 ? 'Offen' : 'Unbekannt'}
            </span>
            <span className="lock-battery">Batterie: {lock.battery}%{lock.batteryCritical ? ' (Kritisch!)' : ''}</span>
          </div>
        )}

        {/* Nuki Sub-Tabs */}
        <div className="nuki-sub-tabs">
          <button className={nukiSubTab === 'activity' ? 'active' : ''} onClick={() => setNukiSubTab('activity')}>Aktivität</button>
          <button className={nukiSubTab === 'users' ? 'active' : ''} onClick={() => setNukiSubTab('users')}>Benutzer</button>
          <button className={nukiSubTab === 'log' ? 'active' : ''} onClick={() => setNukiSubTab('log')}>Protokoll</button>
        </div>

        {/* Activity Sub-Tab */}
        {nukiSubTab === 'activity' && (
          <>
            {/* Summary Cards */}
            <div className="nuki-stats-cards">
              <div className="nuki-stat-card">
                <div className="nuki-stat-label">Heute</div>
                <div className="nuki-stat-value">{todayStats?.total || 0}</div>
                <div className="nuki-stat-detail">
                  {todayStats?.total === 1 ? 'Person' : 'Personen'}
                </div>
              </div>
              <div className="nuki-stat-card">
                <div className="nuki-stat-label">Gestern</div>
                <div className="nuki-stat-value">{yesterdayStats?.total || 0}</div>
                <div className="nuki-stat-detail">
                  {yesterdayStats?.total === 1 ? 'Person' : 'Personen'}
                </div>
              </div>
              <div className="nuki-stat-card">
                <div className="nuki-stat-label">{hasRecentData ? 'Letzte 7 Tage' : 'Gesamt'}</div>
                <div className="nuki-stat-value">{hasRecentData ? last7Unique : allPersons.length}</div>
                <div className="nuki-stat-detail">
                  {hasRecentData ? 'Personen (gesamt)' : `${nukiDailyStats.length} Tage erfasst`}
                </div>
              </div>
            </div>
            {!hasRecentData && lastActivityDate && (
              <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                Letzte Aktivität: {new Date(lastActivityDate + 'T00:00:00').toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
            {nukiDailyStats.length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-secondary)' }}>
                Keine Aktivitätsdaten vorhanden. Daten werden beim nächsten Zugriff auf das Schloss erfasst.
              </div>
            )}

            {/* Besucher pro Tag */}
            {recentDays.length > 0 && (() => {
              const days = recentDays.slice().reverse();
              const dayLabel = (d, opts) => new Date(d + 'T00:00:00').toLocaleDateString('de-AT', opts);
              return (
                <div style={{ marginTop: 24 }}>
                  <ChartCard
                    title={hasRecentData ? 'Besucher der letzten 14 Tage' : `Erfasste Aktivität (${recentDays.length} Tage)`}
                    subtitle="Personen mit Zutritt pro Tag"
                    table={{
                      columns: ['Tag', 'Personen'],
                      rows: days.map(d => [dayLabel(d.date, { weekday: 'short', day: 'numeric', month: 'short' }), d.total]),
                    }}
                  >
                    <ColumnChart
                      categories={days.map(d => dayLabel(d.date, { weekday: 'short', day: 'numeric' }))}
                      tips={days.map(d => dayLabel(d.date, { weekday: 'long', day: 'numeric', month: 'long' }))}
                      series={[{ id: 'visits', name: 'Personen', color: ACCENT, values: days.map(d => d.total) }]}
                      height={220}
                    />
                  </ChartCard>
                </div>
              );
            })()}

            {/* Today's visitors */}
            {todayStats?.persons?.length > 0 && (
              <div className="nuki-persons-section">
                <h3>Heute anwesend</h3>
                <div className="nuki-today-persons">
                  {todayStats.persons.map(name => (
                    <span key={name} className="nuki-person-badge">{name}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Visitors per day - show last 7 days if recent, otherwise all available days */}
            {(() => {
              const daysToShow = hasRecentData
                ? last7.filter(d => d.persons?.length > 0)
                : nukiDailyStats.filter(d => d.persons?.length > 0).slice(0, 14);
              if (daysToShow.length === 0) return null;
              const uniqueCount = hasRecentData ? last7Unique : allPersons.length;
              return (
                <div className="nuki-persons-section">
                  <h3>
                    {hasRecentData
                      ? `Besucher der letzten 7 Tage (${uniqueCount} Personen)`
                      : `Alle erfassten Besucher (${uniqueCount} Personen)`}
                  </h3>
                  <div className="nuki-week-list">
                    {daysToShow.map(day => (
                      <div key={day.date} className="nuki-week-day">
                        <div className="nuki-week-day-label">
                          {new Date(day.date + 'T00:00:00').toLocaleDateString('de-AT', { weekday: 'short', day: 'numeric', month: 'short' })}
                          <span className="nuki-week-day-count">{day.total}</span>
                        </div>
                        <div className="nuki-today-persons">
                          {(day.persons || []).sort((a, b) => a.localeCompare(b, 'de')).map(name => (
                            <span key={name} className="nuki-person-badge">{name}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Besucher-Trend */}
            {monthlyData.length > 1 && (
              <div style={{ marginTop: 32 }}>
                <ChartCard
                  title="Besucher-Trend"
                  subtitle="Durchschnittliche Besucher pro Tag, je Monat"
                  table={{
                    columns: ['Monat', 'Ø pro Tag', 'Versch. Personen', 'Besuche gesamt'],
                    rows: monthlyData.map(m => [m.label, m.avgDaily, m.uniqueTotal, m.totalVisits]),
                  }}
                >
                  <LineChart
                    categories={monthlyData.map(m => m.label)}
                    series={[{ id: 'avg', name: 'Ø Besucher pro Tag', color: ACCENT, values: monthlyData.map(m => m.avgDaily) }]}
                    height={240}
                  />
                </ChartCard>
              </div>
            )}
          </>
        )}

        {/* Users / Matching Sub-Tab */}
        {nukiSubTab === 'users' && (
          <>
            <h3>Studio-Zuordnung</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              Ordne jedes Nuki-Schloss einem Studio zu.
            </p>
            <table className="nuki-auth-table" style={{ marginBottom: 30 }}>
              <thead>
                <tr>
                  <th>Schloss</th>
                  <th>Zugeordnetes Studio</th>
                  <th>Batterie</th>
                </tr>
              </thead>
              <tbody>
                {nukiLocks.map(lock => (
                  <tr key={lock.id}>
                    <td><strong>{lock.name}</strong></td>
                    <td>
                      <select
                        value={nukiLockStudioMappings[lock.id] || ''}
                        onChange={async (e) => {
                          const studioId = e.target.value || null;
                          try {
                            await ownerAPI.saveLockStudioMapping(lock.id, studioId);
                            showMessage('Studio-Zuordnung gespeichert');
                            const res = await ownerAPI.getNukiLockStudioMappings();
                            setNukiLockStudioMappings(res.data);
                          } catch { showMessage('Fehler beim Speichern', 'error'); }
                        }}
                        style={{ width: '100%', padding: '6px 8px' }}
                      >
                        <option value="">Kein Studio</option>
                        {studios.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`nuki-battery-indicator ${lock.battery > 60 ? 'good' : lock.battery > 30 ? 'medium' : lock.battery > 10 ? 'low' : 'critical'}`}>
                        {lock.battery}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>Benutzer-Zuordnung ({nukiUserMatches.length})</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              Ordne Nuki-Benutzer den Kunden zu, um Aktivitäten zuzuweisen.
            </p>
            <table className="nuki-auth-table">
              <thead>
                <tr>
                  <th>Nuki-Name</th>
                  <th>Zugeordneter Kunde</th>
                  <th>Status</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {nukiUserMatches.map(match => (
                  <tr key={match.nukiAuthId}>
                    <td><strong>{match.nukiName}</strong></td>
                    <td>
                      {match.saved || match.autoMatched ? (
                        <span>{match.matchedClientName || '-'}</span>
                      ) : (
                        <select
                          defaultValue=""
                          onChange={(e) => handleSaveNukiMatch(match.nukiAuthId, e.target.value)}
                          style={{ minWidth: 200 }}
                        >
                          <option value="">-- Kunde wählen --</option>
                          {customers.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name} {c.last_name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {match.saved ? (
                        <span className="nuki-match-badge saved">Manuell</span>
                      ) : match.autoMatched ? (
                        <span className="nuki-match-badge auto">Auto</span>
                      ) : (
                        <span className="nuki-match-badge unmatched">Nicht zugeordnet</span>
                      )}
                    </td>
                    <td>
                      {(match.saved || match.autoMatched) && (
                        <button
                          className="btn-link"
                          onClick={() => handleSaveNukiMatch(match.nukiAuthId, null)}
                          title="Zuordnung entfernen"
                        >Entfernen</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 style={{ marginTop: 30 }}>Berechtigungen ({nukiAuths.length} Personen)</h3>
            <table className="nuki-auth-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Kundenstatus</th>
                  <th>Zugriffe</th>
                  <th>Letzter Zugriff</th>
                </tr>
              </thead>
              <tbody>
                {nukiAuths.map(auth => {
                  const match = nukiUserMatches.find(m => m.nukiAuthId === auth.authId);
                  const client = match?.matchedClientId ? customers.find(c => c.id === match.matchedClientId) : null;
                  return (
                  <tr key={auth.id}>
                    <td><strong>{auth.name}</strong></td>
                    <td>
                      <span className={`nuki-auth-status ${auth.enabled ? 'enabled' : 'disabled'}`}>
                        {auth.enabled ? 'Aktiv' : 'Deaktiviert'}
                      </span>
                    </td>
                    <td>
                      {client ? (
                        <span className={`customer-status ${(client.status || '').toLowerCase()}`}>{formatStatus(client.status)}</span>
                      ) : match ? (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>Kunde nicht gefunden</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>{auth.lockCount}</td>
                    <td>{formatDateTime(auth.lastActiveDate)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {/* Log Sub-Tab */}
        {nukiSubTab === 'log' && (
          <>
            <h3>Aktivitätsprotokoll</h3>
            <div className="nuki-log-filters">
              <input
                type="text"
                placeholder="Nach Person filtern..."
                value={nukiLogFilter}
                onChange={(e) => setNukiLogFilter(e.target.value)}
                style={{ flex: 1, maxWidth: 300 }}
              />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {filteredLogs.length} Einträge
              </span>
            </div>
            <table className="nuki-log-table">
              <thead>
                <tr>
                  <th>Datum/Zeit</th>
                  <th>Person</th>
                  <th>Aktion</th>
                  <th>Auslöser</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td>{formatDateTime(log.date)}</td>
                    <td><strong>{log.name || '-'}</strong></td>
                    <td>
                      <span className={`nuki-action-badge ${nukiActionClass(log.actionCode)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td>{log.trigger}{log.autoUnlock ? ' (Auto)' : ''}</td>
                    <td>{log.state === 0 ? 'OK' : log.state === 1 ? 'Blockiert' : log.state === 3 ? 'Abgebrochen' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {nukiLogs.length >= nukiLogLimit && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button onClick={loadMoreNukiLogs} className="btn-secondary">Mehr laden</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderLog = () => {
    const sorted = [...logBookings].sort((a, b) => new Date(b['Created At'] || 0) - new Date(a['Created At'] || 0));
    let filtered = sorted;
    if (logSearch) {
      const q = logSearch.toLowerCase();
      filtered = filtered.filter(b => (b.clientName || '').toLowerCase().includes(q));
    }
    if (logDate) {
      filtered = filtered.filter(b => {
        const ca = b['Created At'];
        return ca && format(new Date(ca), 'yyyy-MM-dd') === logDate;
      });
    }

    // Group by booking day (created_at date)
    const grouped = {};
    filtered.forEach(b => {
      const ca = b['Created At'];
      const dayKey = ca ? format(new Date(ca), 'yyyy-MM-dd') : 'unknown';
      if (!grouped[dayKey]) grouped[dayKey] = [];
      grouped[dayKey].push(b);
    });
    const days = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    return (
      <div className="owner-section">
        <h2>Buchungs-Log</h2>
        <div className="log-filter-bar">
          <div className="log-filter-group">
            <label>Datum</label>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
            />
            {logDate && <button className="log-filter-clear" onClick={() => setLogDate('')} title="Filter zurücksetzen">&times;</button>}
          </div>
          <div className="log-filter-group">
            <label>Name</label>
            <input
              type="text"
              placeholder="Suchen..."
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
            />
            {logSearch && <button className="log-filter-clear" onClick={() => setLogSearch('')} title="Filter zurücksetzen">&times;</button>}
          </div>
          <span className="log-filter-count">{filtered.length} Buchungen</span>
        </div>

        {days.length === 0 && (
          <p style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>Keine Buchungen gefunden</p>
        )}

        {days.map(day => {
          const dayLabel = day === 'unknown' ? 'Unbekannt' : format(new Date(day + 'T00:00:00'), 'EEEE, dd. MMMM yyyy', { locale: de });
          const entries = grouped[day];
          return (
            <div key={day} className="log-day-group">
              <h3 className="log-day-header">{dayLabel}<span className="log-day-count">{entries.length}</span></h3>
              <div className="log-entries">
                {entries.map((b, i) => {
                  const ca = b['Created At'];
                  const bookedTime = ca ? format(new Date(ca), 'HH:mm') : '-';
                  const slotDate = b.Date ? format(new Date(b.Date + 'T00:00:00'), 'EE dd.MM.', { locale: de }) : '-';
                  const slotTime = b['Start Time'] || '-';
                  const status = (b.Status || '').toLowerCase();
                  return (
                    <div key={b.id || i} className={`log-entry ${status === 'cancelled' ? 'log-entry-cancelled' : ''}`}>
                      <span className="log-time">{bookedTime}</span>
                      <span className="log-name">{b.clientName || '-'}{b.Persons > 1 && ` (${b.Persons} Pers.)`}</span>
                      <span className="log-arrow">&rarr;</span>
                      <span className="log-slot">{slotDate} {slotTime}</span>
                      {status === 'cancelled' && <span className="log-cancelled-badge">Storniert</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const statusLabels = { Active: 'Aktiv', Cancelling: 'Kündigungsfrist', Paused: 'Pausiert', Cancelled: 'Ex', Inactive: 'Inaktiv', Prospect: 'Interessent' };
  const statusClass = (s) => s === 'Active' ? 'active' : s === 'Cancelled' || s === 'Inactive' ? 'cancelled' : s === 'Cancelling' ? 'cancelling' : '';

  const handleTaskSort = (field) => {
    setTaskSort(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const taskSortArrow = (field) => {
    if (taskSort.field !== field) return '';
    return taskSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const handleTaskClientClick = (clientId) => {
    setEditingCustomer(clientId);
    navigate('/owner/customers');
  };

  const renderTasks = () => {
    if (!dashboard) return null;
    const { todos } = dashboard;

    const sortedItems = todos && todos.items ? [...todos.items].sort((a, b) => {
      const dir = taskSort.dir === 'asc' ? 1 : -1;
      const valA = a[taskSort.field] || '';
      const valB = b[taskSort.field] || '';
      if (taskSort.field === 'price') return ((Number(valA) || 0) - (Number(valB) || 0)) * dir;
      if (taskSort.field === 'member_since' || taskSort.field === 'member_until') {
        return ((new Date(valA || 0)) - (new Date(valB || 0))) * dir;
      }
      return String(valA).localeCompare(String(valB), 'de') * dir;
    }) : [];

    return (
      <div className="owner-section">
        <h2>Offene Aufgaben {todos && todos.total > 0 ? `(${todos.total})` : ''}</h2>
        {sortedItems.length > 0 ? (
          <table className="customer-table">
            <thead>
              <tr>
                <th onClick={() => handleTaskSort('todo')} style={{ cursor: 'pointer' }}>Aufgabe{taskSortArrow('todo')}</th>
                <th onClick={() => handleTaskSort('client_name')} style={{ cursor: 'pointer' }}>Name{taskSortArrow('client_name')}</th>
                <th onClick={() => handleTaskSort('client_email')} style={{ cursor: 'pointer' }}>E-Mail{taskSortArrow('client_email')}</th>
                <th onClick={() => handleTaskSort('studio_name')} style={{ cursor: 'pointer' }}>Studio{taskSortArrow('studio_name')}</th>
                <th onClick={() => handleTaskSort('status')} style={{ cursor: 'pointer' }}>Status{taskSortArrow('status')}</th>
                <th onClick={() => handleTaskSort('member_since')} style={{ cursor: 'pointer' }}>Eintritt{taskSortArrow('member_since')}</th>
                <th onClick={() => handleTaskSort('member_until')} style={{ cursor: 'pointer' }}>Austritt{taskSortArrow('member_until')}</th>
                <th onClick={() => handleTaskSort('price')} style={{ cursor: 'pointer' }}>Preis{taskSortArrow('price')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, idx) => (
                <tr key={idx}>
                  <td><span className="todo-badge">{item.todo}</span></td>
                  <td><span className="todo-client-link" onClick={() => handleTaskClientClick(item.client_id)}>{item.client_name}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{item.client_email}</td>
                  <td>{item.studio_name || '—'}</td>
                  <td>{item.status && <span className={`todo-status ${statusClass(item.status)}`}>{statusLabels[item.status] || item.status}</span>}</td>
                  <td>{item.member_since ? format(new Date(item.member_since), 'dd.MM.yyyy') : '—'}</td>
                  <td>{item.member_until ? format(new Date(item.member_until), 'dd.MM.yyyy') : '—'}</td>
                  <td>{item.price != null ? `${Number(item.price).toFixed(2)} €` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="daily-empty-state">Keine offenen Aufgaben</div>
        )}
      </div>
    );
  };

  const handleCreateBackup = async () => {
    setBackupCreating(true);
    try {
      const res = await ownerAPI.createBackup();
      showMessage(`Backup erstellt: ${res.data.filename} (${res.data.total_rows} Zeilen)`);
      // Refresh list
      const listRes = await ownerAPI.getBackups();
      setBackups(listRes.data);
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Erstellen des Backups', 'error');
    } finally {
      setBackupCreating(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const res = await ownerAPI.downloadBackup();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup-${format(new Date(), 'yyyy-MM-dd')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMessage('Backup heruntergeladen');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Fehler beim Herunterladen', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderBackups = () => {
    return (
      <div className="owner-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>Backups</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleDownloadBackup} className="btn-secondary">
              Backup herunterladen
            </button>
            <button onClick={handleCreateBackup} className="btn-primary" disabled={backupCreating}>
              {backupCreating ? 'Erstelle...' : '+ Neues Backup'}
            </button>
          </div>
        </div>

        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20 }}>
          Automatisches Backup täglich um 03:00 Uhr. Backups älter als 30 Tage werden automatisch gelöscht.
        </p>

        {backups.length === 0 ? (
          <div className="daily-empty-state">Noch keine Backups vorhanden</div>
        ) : (
          <table className="customer-table">
            <thead>
              <tr>
                <th>Dateiname</th>
                <th>Größe</th>
                <th>Erstellt am</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b, idx) => (
                <tr key={idx}>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{b.name}</td>
                  <td>{formatFileSize(b.size)}</td>
                  <td>{b.created_at ? format(new Date(b.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="owner-dashboard">
      <div className="dashboard-header-row">
        <h1>Eigentümer Dashboard</h1>
        {headerNukiLocks.length > 0 && (
          <div className="header-nuki-batteries">
            {[...headerNukiLocks].sort((a, b) => {
              const nameA = (studios.find(s => s.id === nukiLockStudioMappings[a.id])?.name || a.name).toLowerCase();
              const nameB = (studios.find(s => s.id === nukiLockStudioMappings[b.id])?.name || b.name).toLowerCase();
              if (nameA.includes('obere')) return -1;
              if (nameB.includes('obere')) return 1;
              return 0;
            }).map(lock => {
              const studioId = nukiLockStudioMappings[lock.id];
              const studio = studios.find(s => s.id === studioId);
              const battClass = lock.battery > 60 ? 'good' : lock.battery > 30 ? 'medium' : lock.battery > 10 ? 'low' : 'critical';
              return (
                <div key={lock.id} className={`header-battery-item ${battClass}`} title={`${studio?.name || lock.name}: ${lock.battery}% ${lock.batteryCritical ? '(Kritisch!)' : ''}`}>
                  <span className="header-battery-label">{studio?.name || lock.name}</span>
                  <span className={`header-battery-icon ${battClass}`}></span>
                  <span className="header-battery-value">{lock.battery}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {message && (
        <div className={`owner-message ${message.type}`}>{message.text}</div>
      )}

      {loading ? (
        <div className="loading">Lade...</div>
      ) : (
        <>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'stats' && <OwnerStats dashboard={dashboard} studios={studios} formatCurrency={formatCurrency} />}
          {activeTab === 'customers' && renderCustomers()}
          {activeTab === 'revenue' && <OwnerRevenue dashboard={dashboard} studios={studios} formatCurrency={formatCurrency} />}
          {activeTab === 'bookings' && renderBookings()}
          {activeTab === 'import' && renderImport()}
          {activeTab === 'studios' && renderStudios()}
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'nuki' && renderNuki()}
          {activeTab === 'log' && renderLog()}
          {activeTab === 'tasks' && renderTasks()}
          {activeTab === 'backups' && renderBackups()}
          {activeTab === 'website' && <WebsiteManager showMessage={showMessage} />}
          {activeTab === 'leads' && <LeadsManager showMessage={showMessage} />}
          {activeTab === 'campaigns' && <EmailCampaigns showMessage={showMessage} />}
          {activeTab === 'partner' && <PartnerVerwaltung showMessage={showMessage} />}
        </>
      )}
    </div>
  );
};

export default OwnerDashboard;
